import { useCallback, useEffect, useMemo, useState } from 'react'
import { View, Text, TextInput, Pressable, Platform, ActivityIndicator } from 'react-native'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations'
import { useSnackbar } from '../../../shared/hooks/useSnackbar'
import { extractErrorMessage } from '../../../../helpers/apiClient.helper'
import { formatOrgPlanTierLabel } from '../../helpers/billingDisplay.helper'
import { useAccountAdminCopy } from '../../hooks/useAccountAdminCopy'
import {
  ACCOUNT_INVOICE_DETAILS_TEST_IDS,
  ORG_INVOICE_DETAILS_COPY,
  ORG_INVOICE_PROFILE_LIMITS,
} from '../../constants/orgInvoiceProfile.constants'
import {
  orgInvoiceProfileFromAccount,
  sanitizeOrgInvoiceProfileField,
  validateOrgInvoiceProfilePatch,
} from '../../helpers/orgInvoiceProfile.helper'
import type { OrgInvoiceProfileFormInput } from '../../helpers/orgInvoiceProfile.helper'
import type { OrgInvoiceProfilePatch } from '../../helpers/orgInvoiceProfile.helper'
import type { Theme } from '../../../theme/getTheme'
import type { B2bAccountDto } from '../../types/b2bAccount.types'
import { getAccountInvoiceDetailsSectionStyles } from './AccountInvoiceDetailsSection.styles'

interface AccountInvoiceDetailsSectionProps {
  account: B2bAccountDto
  theme: Theme
  onSave: (patch: OrgInvoiceProfilePatch) => Promise<void>
  /** When stacked below billing on the Billing & invoice tab. */
  showTopDivider?: boolean
}

export function AccountInvoiceDetailsSection({
  account,
  theme,
  onSave,
  showTopDivider = false,
}: AccountInvoiceDetailsSectionProps) {
  const copy = useAccountAdminCopy()
  const styles = useMemo(() => getAccountInvoiceDetailsSectionStyles(theme), [theme])
  const { showSnackbar } = useSnackbar()
  const [form, setForm] = useState(() => orgInvoiceProfileFromAccount(account))
  const [localError, setLocalError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setForm(orgInvoiceProfileFromAccount(account))
  }, [account])

  const setInvoiceField = useCallback(
    (key: keyof OrgInvoiceProfileFormInput, rawValue: string) => {
      setLocalError(null)
      setForm((prev) => ({
        ...prev,
        [key]: sanitizeOrgInvoiceProfileField(key, prev, rawValue),
      }))
    },
    [],
  )

  const handleSave = useCallback(async () => {
    const validation = validateOrgInvoiceProfilePatch(form)
    if (!validation.ok) {
      setLocalError(validation.error)
      return
    }
    setLocalError(null)
    setSaving(true)
    try {
      await onSave(validation.value)
      showSnackbar(ORG_INVOICE_DETAILS_COPY.SAVE_SUCCESS, 'success')
    } catch (e) {
      setLocalError(extractErrorMessage(e, ORG_INVOICE_DETAILS_COPY.SAVE_FAILURE))
    } finally {
      setSaving(false)
    }
  }, [form, onSave, showSnackbar])

  const L = ORG_INVOICE_PROFILE_LIMITS
  const c = ORG_INVOICE_DETAILS_COPY

  return (
    <View
      style={[styles.section, showTopDivider && styles.sectionWithTopDivider]}
      testID={ACCOUNT_INVOICE_DETAILS_TEST_IDS.SECTION}
    >
      <Text style={styles.title}>{c.SECTION_TITLE}</Text>
      <Text style={styles.intro}>{c.INTRO}</Text>
      <Text style={styles.intro}>{c.READ_ONLY_NOTE}</Text>

      <View style={styles.readOnlyRow}>
        <View style={styles.readOnlyItem}>
          <Text style={styles.readOnlyLabel}>{c.DERIVED_COMPANY}</Text>
          <Text style={styles.readOnlyValue}>{account.displayName ?? account.name}</Text>
        </View>
        <View style={styles.readOnlyItem}>
          <Text style={styles.readOnlyLabel}>{c.DERIVED_PACKAGE}</Text>
          <Text style={styles.readOnlyValue}>
            {formatOrgPlanTierLabel(account.planTier, copy.billing)}
          </Text>
        </View>
        <View style={styles.readOnlyItem}>
          <Text style={styles.readOnlyLabel}>{c.DERIVED_SEATS}</Text>
          <Text style={styles.readOnlyValue}>{account.maxSeats}</Text>
        </View>
      </View>

      <View style={styles.fieldGroup}>
        <Text style={styles.label}>{c.BILLING_CONTACT_NAME_LABEL}</Text>
        <TextInput
          style={styles.input}
          value={form.billingContactName}
          onChangeText={(text) => setInvoiceField('billingContactName', text)}
          placeholder={c.BILLING_CONTACT_NAME_PLACEHOLDER}
          placeholderTextColor={theme.gray[500]}
        />
      </View>

      <View style={styles.fieldGroup}>
        <Text style={styles.label}>{c.BILLING_EMAIL_LABEL}</Text>
        <TextInput
          style={styles.input}
          value={form.billingEmail}
          onChangeText={(text) => setInvoiceField('billingEmail', text)}
          placeholder={c.BILLING_EMAIL_PLACEHOLDER}
          placeholderTextColor={theme.gray[500]}
          autoCapitalize="none"
          keyboardType="email-address"
          maxLength={L.BILLING_EMAIL}
        />
      </View>

      <View style={styles.fieldGroup}>
        <Text style={styles.label}>{c.BILLING_ADDRESS_LINE1_LABEL}</Text>
        <TextInput
          style={styles.input}
          value={form.billingAddressLine1}
          onChangeText={(text) => setInvoiceField('billingAddressLine1', text)}
          placeholder={c.BILLING_ADDRESS_LINE1_PLACEHOLDER}
          placeholderTextColor={theme.gray[500]}
          maxLength={L.BILLING_ADDRESS_LINE}
        />
        <TextInput
          style={[styles.input, styles.inputMultiline]}
          value={form.billingAddressLine2}
          onChangeText={(text) => setInvoiceField('billingAddressLine2', text)}
          placeholder={c.BILLING_ADDRESS_LINE2_PLACEHOLDER}
          placeholderTextColor={theme.gray[500]}
          maxLength={L.BILLING_ADDRESS_LINE}
        />
      </View>

      <View style={styles.fieldGroup}>
        <Text style={styles.label}>{c.BILLING_CITY_LABEL}</Text>
        <TextInput
          style={styles.input}
          value={form.billingCity}
          onChangeText={(text) => setInvoiceField('billingCity', text)}
          maxLength={L.BILLING_CITY}
          placeholderTextColor={theme.gray[500]}
        />
      </View>

      <View style={styles.fieldGroup}>
        <Text style={styles.label}>{c.BILLING_STATE_LABEL}</Text>
        <TextInput
          style={styles.input}
          value={form.billingStateOrRegion}
          onChangeText={(text) => setInvoiceField('billingStateOrRegion', text)}
          maxLength={L.BILLING_STATE_OR_REGION}
          placeholderTextColor={theme.gray[500]}
        />
      </View>

      <View style={styles.fieldGroup}>
        <Text style={styles.label}>{c.BILLING_POSTAL_LABEL}</Text>
        <TextInput
          style={styles.input}
          value={form.billingPostalCode}
          onChangeText={(text) => setInvoiceField('billingPostalCode', text)}
          maxLength={L.BILLING_POSTAL_CODE}
          placeholderTextColor={theme.gray[500]}
        />
      </View>

      <View style={styles.fieldGroup}>
        <Text style={styles.label}>{c.BILLING_COUNTRY_LABEL}</Text>
        <TextInput
          style={styles.input}
          value={form.billingCountry}
          onChangeText={(text) => setInvoiceField('billingCountry', text)}
          placeholder={c.BILLING_COUNTRY_PLACEHOLDER}
          placeholderTextColor={theme.gray[500]}
          autoCapitalize="characters"
          maxLength={L.BILLING_COUNTRY}
        />
      </View>

      <View style={styles.fieldGroup}>
        <Text style={styles.label}>{c.COMPANY_REG_LABEL}</Text>
        <TextInput
          style={styles.input}
          value={form.companyRegistrationNumber}
          onChangeText={(text) => setInvoiceField('companyRegistrationNumber', text)}
          placeholder={c.COMPANY_REG_PLACEHOLDER}
          placeholderTextColor={theme.gray[500]}
          maxLength={L.COMPANY_REGISTRATION_NUMBER}
        />
      </View>

      <View style={styles.fieldGroup}>
        <Text style={styles.label}>{c.VAT_LABEL}</Text>
        <TextInput
          style={styles.input}
          value={form.vatNumber}
          onChangeText={(text) => setInvoiceField('vatNumber', text)}
          placeholder={c.VAT_PLACEHOLDER}
          placeholderTextColor={theme.gray[500]}
          maxLength={L.VAT_NUMBER}
        />
      </View>

      <View style={styles.fieldGroup}>
        <Text style={styles.label}>{c.PO_LABEL}</Text>
        <TextInput
          style={styles.input}
          value={form.purchaseOrderNumber}
          onChangeText={(text) => setInvoiceField('purchaseOrderNumber', text)}
          placeholder={c.PO_PLACEHOLDER}
          placeholderTextColor={theme.gray[500]}
          maxLength={L.PURCHASE_ORDER_NUMBER}
        />
      </View>

      {localError ? <Text style={styles.errorText}>{localError}</Text> : null}

      <Pressable
        accessibilityRole="button"
        testID={ACCOUNT_INVOICE_DETAILS_TEST_IDS.SAVE_BUTTON}
        disabled={saving}
        onPress={() => void handleSave()}
        style={({ pressed, hovered }) => [
          styles.saveButton,
          saving && styles.saveButtonDisabled,
          pressed && styles.saveButtonPressed,
          hovered && Platform.OS === 'web' && styles.saveButtonPressed,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
        ]}
      >
        {saving ? (
          <ActivityIndicator size="small" color="#fff" />
        ) : (
          <Text style={styles.saveButtonText}>{c.SAVE_BUTTON}</Text>
        )}
      </Pressable>
    </View>
  )
}
